
import { api, type RequestOptions } from './base';
import { getValidToken } from '@/features/auth';
import { config } from '@/shared/lib/config';
import { compressImageFile } from '@/shared/lib/compress-image';
import { cachedFetch, invalidateCached } from '@/shared/lib/refCache';

export type SprintStatus = 'planned' | 'active' | 'closed';
export type CarryDestination = 'next' | 'backlog';
export type TaskVisibility = 'full' | 'linked' | 'none';

export interface SprintSummary {
  tasksTotal: number;
  tasksDone: number;
  velocityMinutes: number;
  plannedMinutes: number;
  spentMinutes: number;
  carriedOver: number;
  carriedToSprintId: string | null;
  carriedAwaiting?: number;
}

export interface Sprint {
  id: string;
  number: number | null;
  key: string;
  supportGroupId: string;
  name: string;
  status: SprintStatus;
  goal: string | null;
  startsOn: string;
  endsOn: string;
  calendarId: string | null;
  capacityMinutes: number | null;
  closedAt: string | null;
  closedBy: string | null;
  summary: SprintSummary | null;
  isCommitted: boolean;
  committedAt: string | null;
  committedBy: string | null;
  overdue: boolean;
}

export interface CapacityRow {
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
  capacityMinutes: number | null;
  allocatedMinutes: number;
  freeMinutes: number | null;
  overloaded: boolean;
  memberRole: string | null;
  inactive: boolean;
}

export interface CapacityReport {
  sprintId: string;
  rows: CapacityRow[];
  totals: {
    capacityMinutes: number;
    allocatedMinutes: number;
    freeMinutes: number;
    unassignedMinutes: number;
  };
}

export interface SprintSnapshotTask {
  id: string;
  key: string;
  title: string;
  status: string;
  estimateWorkMinutes: number | null;
  spentMinutes: number;
  assignees: { id: string; displayName: string }[];
}

export interface SprintSnapshot {
  name: string;
  goal: string | null;
  status: string;
  startsOn: string;
  endsOn: string;
  isCommitted: boolean;
  tasks: SprintSnapshotTask[];
  capacities: { userId: string; displayName: string; capacityMinutes: number }[];
  totals: { plannedMinutes: number; capacityMinutes: number; tasksTotal: number };
}

export interface SprintChange {
  field: string;
  label: string;
  from: string | null;
  to: string | null;
}

export interface SprintVersionStats {
  added: number;
  removed: number;
  estimate: number;
  assignees: number;
  capacity: number;
  sprint: number;
}

export interface SprintVersion {
  id: string;
  versionNo: number;
  actorId: string | null;
  actor: { id: string; displayName: string } | null;
  createdAt: string;
  changes: SprintChange[];
  stats?: SprintVersionStats;
  snapshot?: SprintSnapshot;
  against?: number | null;
}

export interface Paged<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export const TASK_PRIORITIES = ['urgent', 'high', 'normal', 'low'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

export interface TaskLabel {
  key: string;
  label: string;
  color: string | null;
  sortOrder: number;
  archivedAt: string | null;
}

export interface TaskRelationRef {
  id: string;
  key: string;
  title: string;
  status: string;
  done: boolean;
  dueOn?: string | null;
}

export interface SubtaskSummary {
  total: number;
  done: number;
  estimateMinutes?: number;
  spentMinutes?: number;
  withoutEstimate?: number;
}

export interface TaskTreeRef {
  id: string;
  key: string;
  title: string;
  status: string;
  done: boolean;
  sprint: { id: string; key: string; name: string } | null;
  estimateWorkMinutes: number | null;
  spentMinutes: number;
  dueOn: string | null;
  assignees: TaskAssigneeRef[];
}

export interface TaskStatus {
  key: string;
  label: string;
  sortOrder: number;
  isDone: boolean;
  isTerminal: boolean;
  color: string | null;
  wipLimit: number | null;
  awaitsConfirmation: boolean;
  requiresConfirmation: boolean;
  archivedAt: string | null;
}

export interface TaskTicketRef {
  id: string;
  number: number | null;
  subject: string;
  status: string;
  confirmed: boolean;
  finished: boolean;
  priorityWeight: number | null;
}

export interface TaskAssigneeRef {
  id: string;
  displayName: string;
  avatarUrl?: string | null;
}

export interface Task {
  id: string;
  number: number | null;
  groupNumber: number | null;
  key: string;
  sprintId: string | null;
  supportGroupId: string;
  title: string;
  description: string | null;
  status: string;
  estimateWorkMinutes?: number | null;
  spentWorkMinutes?: number;
  spentManualMinutes?: number | null;
  assigneeIds?: string[];
  assignees?: TaskAssigneeRef[];
  boardOrder: number;
  tickets: TaskTicketRef[];
  priorityWeight: number | null;
  priority?: TaskPriority;
  dueOn?: string | null;
  planStartOn?: string | null;
  planEndOn?: string | null;
  carryCount?: number;
  carriedFrom?: { id: string; key: string; name: string } | null;
  parentTaskId?: string | null;
  triagedAt?: string | null;
  statusChangedAt?: string | null;
  labels?: TaskLabel[];
  blocks?: TaskRelationRef[];
  blockedBy?: TaskRelationRef[];
  subtasks?: SubtaskSummary;
  parent?: TaskTreeRef | null;
  children?: TaskTreeRef[];
  doneAt: string | null;
  createdBy?: string | null;
  createdAt: string | null;
  visibility: TaskVisibility;
  commentsCount: number;
  attachmentsCount: number;
}

export interface TaskAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  url: string;
  thumbUrl?: string;
  createdAt?: string;
}

export interface TaskMention {
  id: string;
  displayName: string;
}

export interface TaskComment {
  id: string;
  taskId: string;
  authorId: string;
  author: { id: string; displayName: string; avatarUrl?: string | null } | null;
  body: string;
  parentCommentId: string | null;
  replyToCommentId: string | null;
  editedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  attachments: TaskAttachment[];
  mentions: TaskMention[];
}

export interface TaskParticipantRef {
  id: string;
  displayName: string;
  addedBy: string | null;
}

export type TaskEventKind =
  | 'task_created'
  | 'status_changed'
  | 'assignees_changed'
  | 'sprint_changed'
  | 'estimate_changed'
  | 'spent_manual_changed'
  | 'title_changed'
  | 'description_changed'
  | 'ticket_linked'
  | 'ticket_unlinked'
  | 'commented'
  | 'comment_deleted'
  | 'participant_added'
  | 'participant_removed'
  | 'attachment_added'
  | 'attachment_removed'
  | 'sprint_created'
  | 'sprint_started'
  | 'sprint_closed'
  | 'sprint_committed'
  | 'sprint_uncommitted'
  | 'sprint_deleted'
  | 'capacity_changed'
  | 'priority_changed'
  | 'due_changed'
  | 'labels_changed'
  | 'parent_changed'
  | 'link_added'
  | 'link_removed'
  | 'triaged'
  | 'sprint_carried'
  | 'plan_start_changed'
  | 'plan_end_changed'
  | 'ticket_finished';

export interface TaskEventQuery {
  limit?: number;
  offset?: number;
  kinds?: TaskEventKind[];
  actorId?: string;
  taskId?: string;
  since?: string;
}

export interface TaskEventFacets {
  actors: { id: string; displayName: string; count: number }[];
  kinds: { kind: TaskEventKind; count: number }[];
}

export interface TaskEvent {
  id: string;
  taskId: string | null;
  sprintId: string | null;
  supportGroupId: string;
  actorId: string | null;
  actor: { id: string; displayName: string } | null;
  kind: TaskEventKind;
  payload: Record<string, unknown> | null;
  createdAt: string;
  task?: { id: string; key: string; title: string } | null;
}

export interface SprintMetrics {
  sprintId: string;
  supportGroupId: string;
  name: string;
  status: string;
  startsOn: string;
  endsOn: string;
  capacityMinutes: number | null;
  velocityMinutes: number;
  plannedMinutes: number;
  spentMinutes: number;
  tasksTotal: number;
  tasksDone: number;
  carriedOver: number;
}

export interface CalendarDay {
  date: string;
  working: boolean;
  workMinutes: number;
  exception: 'holiday' | 'workday' | null;
}

export interface CalendarRange {
  timezone: string;
  days: CalendarDay[];
}

export interface BoardView {
  id: string;
  supportGroupId: string;
  name: string;
  query: Record<string, string>;
  isShared: boolean;
  sortOrder: number;
  own: boolean;
}

export interface ScopeChangeItem {
  id: string;
  key: string;
  title: string;
  estimateWorkMinutes: number | null;
}

export interface SprintReport {
  sprintId: string;
  name: string;
  status: string;
  startsOn: string;
  endsOn: string;
  facts: {
    tasksTotal: number;
    tasksDone: number;
    plannedMinutes: number;
    spentMinutes: number;
    velocityMinutes: number;
    capacityMinutes: number | null;
    carriedOver: number | null;
    carriedAwaiting?: number | null;
  };
  scope: {
    committedAt: string | null;
    committedTasks: number;
    committedMinutes: number;
    added: ScopeChangeItem[];
    removed: ScopeChangeItem[];
    addedMinutes: number;
    removedMinutes: number;
  } | null;
  byLabel: Array<{
    key: string | null;
    label: string;
    color: string | null;
    tasks: number;
    doneTasks: number;
    minutes: number;
  }>;
  burndown: BurndownPoint[];
}

export interface BurndownPoint {
  date: string;
  remainingMinutes: number;
}

function toPaged<T>(
  r: { data: T[]; pagination?: { total: number; limit: number; offset: number } },
  page: { limit?: number; offset?: number },
): Paged<T> {
  const items = r.data ?? [];
  return {
    items,
    total: r.pagination?.total ?? items.length,
    limit: r.pagination?.limit ?? page.limit ?? items.length,
    offset: r.pagination?.offset ?? page.offset ?? 0,
  };
}

function eventParams(query: TaskEventQuery): Record<string, string | number | undefined> {
  return {
    limit: query.limit,
    offset: query.offset,
    kinds: query.kinds?.length ? query.kinds.join(',') : undefined,
    actorId: query.actorId,
    taskId: query.taskId,
    since: query.since,
  };
}

function qs(params: Record<string, string | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const str = search.toString();
  return str ? `?${str}` : '';
}

export const sprintsApi = {
  list: (params: { groupId?: string; status?: string; includeClosed?: boolean } = {}) =>
    api.get<Sprint[]>(`/api/v1/sprints${qs(params)}`).then((r) => r.data),

  get: (id: string) => api.get<Sprint>(`/api/v1/sprints/${id}`).then((r) => r.data),

  capacity: (groupId: string, startsOn: string, endsOn: string) =>
    api
      .get<{ capacityMinutes: number }>(`/api/v1/sprints/capacity${qs({ groupId, startsOn, endsOn })}`)
      .then((r) => r.data.capacityMinutes),

  create: (body: {
    supportGroupId: string;
    name: string;
    goal?: string | null;
    startsOn: string;
    endsOn: string;
  }) =>
    api.post<Sprint>('/api/v1/sprints', body, { toast: { success: 'Спринт создан' } })
      .then((r) => r.data),

  update: (
    id: string,
    body: { name?: string; goal?: string | null; startsOn?: string; endsOn?: string },
  ) => api.patch<Sprint>(`/api/v1/sprints/${id}`, body).then((r) => r.data),

  moveTasks: (id: string, taskIds: string[]) =>
    api
      .post<{ moved: number }>(`/api/v1/sprints/${id}/tasks`, { taskIds })
      .then((r) => r.data),

  versions: (id: string, page: { limit?: number; offset?: number } = {}) =>
    api
      .get<SprintVersion[]>(`/api/v1/sprints/${id}/versions`, page)
      .then((r) => toPaged(r, page)),

  version: (id: string, versionNo: number, against?: number | null) =>
    api
      .get<SprintVersion>(`/api/v1/sprints/${id}/versions/${versionNo}`, {
        against: against ?? undefined,
      })
      .then((r) => r.data),

  capacityReport: (id: string) =>
    api.get<CapacityReport>(`/api/v1/sprints/${id}/capacity`).then((r) => r.data),

  setCapacity: (id: string, userId: string, capacityMinutes: number) =>
    api
      .put<CapacityReport>(`/api/v1/sprints/${id}/capacity/${userId}`, { capacityMinutes })
      .then((r) => r.data),

  removeCapacity: (id: string, userId: string) =>
    api.delete<CapacityReport>(`/api/v1/sprints/${id}/capacity/${userId}`).then((r) => r.data),

  commit: (id: string) =>
    api
      .post<Sprint>(`/api/v1/sprints/${id}/commit`, {}, { toast: { success: 'Спринт сформирован' } })
      .then((r) => r.data),

  uncommit: (id: string) =>
    api
      .post<Sprint>(`/api/v1/sprints/${id}/uncommit`, {}, { toast: { success: 'Спринт расформирован' } })
      .then((r) => r.data),

  start: (id: string) =>
    api.post<Sprint>(`/api/v1/sprints/${id}/start`, {}, { toast: { success: 'Спринт стартовал' } })
      .then((r) => r.data),

  close: (
    id: string,
    body: { carry?: Record<string, CarryDestination>; defaultDestination?: CarryDestination },
  ) =>
    api.post<Sprint>(`/api/v1/sprints/${id}/close`, body, {
        toast: { success: 'Спринт закрыт' },
      })
      .then((r) => r.data),

  remove: (id: string) =>
    api.delete(`/api/v1/sprints/${id}`, { toast: { success: 'Спринт удалён' } }).then(() => undefined),

  metrics: (groupId?: string) =>
    api.get<SprintMetrics[]>(`/api/v1/sprints/metrics${qs({ groupId })}`).then((r) => r.data),

  burndown: (id: string) =>
    api.get<BurndownPoint[]>(`/api/v1/sprints/${id}/burndown`).then((r) => r.data),

  report: (id: string) => api.get<SprintReport>(`/api/v1/sprints/${id}/report`).then((r) => r.data),

  calendar: (groupId: string, from: string, to: string) =>
    cachedFetch(`sprint-calendar:${groupId}:${from}:${to}`, 5 * 60_000, () =>
      api.get<CalendarRange>(`/api/v1/sprints/calendar${qs({ groupId, from, to })}`).then((r) => r.data),
    ),

  views: (groupId: string) =>
    api.get<BoardView[]>(`/api/v1/sprints/views${qs({ groupId })}`).then((r) => r.data),

  createView: (body: {
    supportGroupId: string;
    name: string;
    query: Record<string, string>;
    isShared?: boolean;
  }) =>
    api
      .post<BoardView>('/api/v1/sprints/views', body, { toast: { success: 'Представление сохранено' } })
      .then((r) => r.data),

  updateView: (
    id: string,
    body: { name?: string; query?: Record<string, string>; isShared?: boolean; sortOrder?: number },
  ) => api.patch<BoardView>(`/api/v1/sprints/views/${id}`, body).then((r) => r.data),

  deleteView: (id: string) =>
    api.delete(`/api/v1/sprints/views/${id}`).then(() => undefined),
  events: (id: string, query: TaskEventQuery = {}, opts?: RequestOptions) =>
    api
      .get<TaskEvent[]>(`/api/v1/sprints/${id}/events`, eventParams(query), opts)
      .then((r) => toPaged(r, query)),

  eventFacets: (id: string) =>
    api.get<TaskEventFacets>(`/api/v1/sprints/${id}/events/facets`, undefined, { toast: false }).then((r) => r.data),
};

export const tasksApi = {
  statuses: () =>
    cachedFetch('task-statuses', 5 * 60_000, () =>
      api.get<TaskStatus[]>('/api/v1/tasks/statuses').then((r) => r.data),
    ),

  labels: () =>
    cachedFetch('task-labels', 5 * 60_000, () =>
      api.get<TaskLabel[]>('/api/v1/tasks/labels').then((r) => r.data),
    ),

  list: (
    params: {
      groupId?: string;
      sprintId?: string;
      backlog?: boolean;
      ticketId?: string;
      status?: string;
      assignee?: 'me';
      q?: string;
    } = {},
  ) => api.get<Task[]>(`/api/v1/tasks${qs(params)}`).then((r) => r.data),

  get: (id: string) => api.get<Task>(`/api/v1/tasks/${id}`).then((r) => r.data),

  create: (body: {
    supportGroupId: string;
    sprintId?: string | null;
    title: string;
    description?: string | null;
    estimateWorkMinutes?: number | null;
    assigneeIds?: string[];
    ticketIds?: string[];
    priority?: TaskPriority;
    dueOn?: string | null;
    labels?: string[];
    parentTaskId?: string | null;
  }) =>
    api.post<Task>('/api/v1/tasks', body, { toast: { success: 'Задача заведена' } })
      .then((r) => r.data),

  update: (
    id: string,
    body: {
      title?: string;
      description?: string | null;
      estimateWorkMinutes?: number | null;
      sprintId?: string | null;
      priority?: TaskPriority;
      dueOn?: string | null;
      labels?: string[];
      parentTaskId?: string | null;
      triaged?: boolean;
      planStartOn?: string | null;
      planEndOn?: string | null;
    },
  ) => api.patch<Task>(`/api/v1/tasks/${id}`, body).then((r) => r.data),

  setStatus: (id: string, status: string) =>
    api.put<Task>(`/api/v1/tasks/${id}/status`, { status }).then((r) => r.data),

  setPosition: (id: string, status: string, orderedIds: string[]) =>
    api.put<Task>(`/api/v1/tasks/${id}/position`, { status, orderedIds }).then((r) => r.data),

  setSpent: (id: string, spentManualMinutes: number | null) =>
    api.put<Task>(`/api/v1/tasks/${id}/spent`, { spentManualMinutes }).then((r) => r.data),

  setAssignees: (id: string, userIds: string[]) =>
    api.put<Task>(`/api/v1/tasks/${id}/assignees`, { userIds }).then((r) => r.data),

  linkTicket: (id: string, ticketId: string) =>
    api.post<Task>(`/api/v1/tasks/${id}/tickets`, { ticketId }, { toast: { success: 'Заявка связана' } })
      .then((r) => r.data),

  unlinkTicket: (id: string, ticketId: string) =>
    api.delete<Task>(`/api/v1/tasks/${id}/tickets/${ticketId}`).then((r) => r.data),

  addLink: (id: string, toTaskId: string) =>
    api.post<Task>(`/api/v1/tasks/${id}/links`, { toTaskId }).then((r) => r.data),

  removeLink: (id: string, toTaskId: string) =>
    api.delete<Task>(`/api/v1/tasks/${id}/links/${toTaskId}`).then((r) => r.data),
  comments: (id: string) => api.get<TaskComment[]>(`/api/v1/tasks/${id}/comments`).then((r) => r.data),
  addComment: (
    id: string,
    body: { body: string; attachmentIds?: string[]; replyToCommentId?: string | null; parentCommentId?: string | null },
  ) =>
    api.post<TaskComment>(`/api/v1/tasks/${id}/comments`, body).then((r) => r.data),
  editComment: (id: string, commentId: string, body: string) =>
    api.patch<TaskComment>(`/api/v1/tasks/${id}/comments/${commentId}`, { body }).then((r) => r.data),
  deleteComment: (id: string, commentId: string) =>
    api.delete<TaskComment>(`/api/v1/tasks/${id}/comments/${commentId}`).then((r) => r.data),
  mentionable: (id: string) =>
    api.get<(TaskMention & { kind: 'user' | 'group' })[]>(`/api/v1/tasks/${id}/mentionable`).then((r) => r.data),
  participants: (id: string) => api.get<TaskParticipantRef[]>(`/api/v1/tasks/${id}/participants`).then((r) => r.data),
  addParticipant: (id: string, userId: string) =>
    api.post<TaskParticipantRef[]>(`/api/v1/tasks/${id}/participants`, { userId }).then((r) => r.data),
  removeParticipant: (id: string, userId: string) =>
    api.delete<TaskParticipantRef[]>(`/api/v1/tasks/${id}/participants/${encodeURIComponent(userId)}`).then((r) => r.data),
  attachments: (id: string) => api.get<TaskAttachment[]>(`/api/v1/tasks/${id}/attachments`).then((r) => r.data),
  deleteAttachment: (id: string, attachmentId: string) =>
    api.delete<{ ok: true }>(`/api/v1/tasks/${id}/attachments/${attachmentId}`).then((r) => r.data),
  events: (id: string, page: { limit?: number; offset?: number } = {}) =>
    api.get<TaskEvent[]>(`/api/v1/tasks/${id}/events`, page).then((r) => toPaged(r, page)),
  uploadAttachment: (
    id: string,
    file: File,
    owner: 'task' | 'comment',
    onProgress?: (percent: number) => void,
  ): Promise<TaskAttachment> =>
    new Promise((resolve, reject) => {
      Promise.all([getValidToken(), compressImageFile(file)])
        .then(([token, compressed]) => {
          const form = new FormData();
          form.append('file', compressed);
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${config.apiUrl}/api/v1/tasks/${id}/attachments?owner=${owner}`);
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
          };
          xhr.onload = () => {
            try {
              const json = JSON.parse(xhr.responseText) as { data?: TaskAttachment; error?: { message?: string } };
              if (xhr.status >= 200 && xhr.status < 300 && json.data) resolve(json.data);
              else reject(new Error(json.error?.message ?? `Ошибка загрузки (${xhr.status})`));
            } catch {
              reject(new Error(`Ошибка загрузки (${xhr.status})`));
            }
          };
          xhr.onerror = () => reject(new Error('Сетевая ошибка при загрузке файла'));
          xhr.send(form);
        })
        .catch(reject);
    }),
};

function invalidateStatuses<T>(res: T): T {
  invalidateCached('task-statuses');
  return res;
}

export const adminTaskStatusesApi = {
  list: () => api.get<TaskStatus[]>('/api/v1/admin/task-statuses').then((r) => r.data),

  create: (body: {
    key: string;
    label: string;
    sortOrder?: number;
    isDone?: boolean;
    isTerminal?: boolean;
    color?: string | null;
    wipLimit?: number | null;
    awaitsConfirmation?: boolean;
    requiresConfirmation?: boolean;
  }) =>
    api.post<TaskStatus>('/api/v1/admin/task-statuses', body, { toast: { success: 'Статус добавлен' } })
      .then((r) => r.data)
      .then(invalidateStatuses),

  update: (
    key: string,
    body: {
      label?: string;
      sortOrder?: number;
      isDone?: boolean;
      isTerminal?: boolean;
      color?: string | null;
      wipLimit?: number | null;
      archived?: boolean;
      awaitsConfirmation?: boolean;
      requiresConfirmation?: boolean;
    },
  ) =>
    api
      .patch<TaskStatus>(`/api/v1/admin/task-statuses/${key}`, body)
      .then((r) => r.data)
      .then(invalidateStatuses),
};

function invalidateLabels<T>(res: T): T {
  invalidateCached('task-labels');
  return res;
}

export const adminTaskLabelsApi = {
  list: () => api.get<TaskLabel[]>('/api/v1/admin/task-labels').then((r) => r.data),

  create: (body: { key: string; label: string; color?: string | null; sortOrder?: number }) =>
    api
      .post<TaskLabel>('/api/v1/admin/task-labels', body, { toast: { success: 'Метка добавлена' } })
      .then((r) => r.data)
      .then(invalidateLabels),

  update: (
    key: string,
    body: { label?: string; color?: string | null; sortOrder?: number; archived?: boolean },
  ) =>
    api
      .patch<TaskLabel>(`/api/v1/admin/task-labels/${key}`, body)
      .then((r) => r.data)
      .then(invalidateLabels),
};

export function formatWorkMinutes(min: number | null | undefined): string {
  if (min == null) return '—';
  if (min < 90) return `${Math.round(min)} мин`;
  const hours = min / 60;
  const rounded = hours < 10 ? hours.toFixed(1).replace('.', ',') : String(Math.round(hours));
  return `${rounded} ч`;
}
